import React from "react";
import { Progress, Typography } from "antd";
import { CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import type { CommercialInvoiceValidityBreakdown, ValidityScoreSource } from "./commercialInvoiceValidity";
import { validityScoreColor } from "./commercialInvoiceValidity";

const { Text } = Typography;

const PASS_GREEN = "#52c41a";
const PASS_BG = "#f6ffed";
const PASS_BORDER = "#b7eb8f";
const FAIL_RED = "#ff4d4f";
const FAIL_BG = "#fff2f0";
const FAIL_BORDER = "#ffccc7";

const formatScoreMethod = (
  source: ValidityScoreSource,
  detail: string,
  compact: boolean
): string => {
  const prefix = source === "ai" ? "AI" : compact ? "Auto" : "Automatic";
  return `${prefix} · ${detail}`;
};

export interface CommercialInvoiceValidityDisplayProps {
  breakdown: CommercialInvoiceValidityBreakdown;
  compact?: boolean;
  extra?: React.ReactNode;
  /** Show footnote that scores are automatic, not AI. */
  showAiNote?: boolean;
  /** AI deep check still running — automatic scores shown first. */
  aiCheckPending?: boolean;
}

const ScoreRow: React.FC<{
  label: string;
  method?: string;
  score: number | null;
  detail?: string;
  compact?: boolean;
}> = ({ label, method, score, detail, compact }) => (
  <div style={{ marginTop: compact ? 2 : 4 }}>
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        gap: 6,
        marginBottom: 1,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <Text style={{ fontSize: compact ? 10 : 11, lineHeight: 1.2 }}>{label}</Text>
        {method ? (
          <Text type="secondary" style={{ fontSize: 9, display: "block", lineHeight: 1.2 }}>
            {method}
          </Text>
        ) : null}
      </div>
      <Text strong style={{ fontSize: compact ? 10 : 11, lineHeight: 1.2, flexShrink: 0 }}>
        {score == null ? "—" : `${score}%`}
      </Text>
    </div>
    {score != null ? (
      <Progress
        percent={score}
        size="small"
        showInfo={false}
        strokeColor={validityScoreColor(score)}
        strokeWidth={compact ? 3 : 4}
        style={{ marginBottom: 0, lineHeight: 1 }}
      />
    ) : detail ? (
      <Text type="secondary" style={{ fontSize: 9, lineHeight: 1.2 }}>
        {detail}
      </Text>
    ) : null}
  </div>
);

/** Three-step CI validity: non-proforma check + completeness + booking match scores. */
export const CommercialInvoiceValidityDisplay: React.FC<
  CommercialInvoiceValidityDisplayProps
> = ({ breakdown, compact = false, extra, showAiNote = false, aiCheckPending = false }) => {
  if (!breakdown) return null;

  const passed = breakdown.isNonProforma ?? true;
  const completenessScore = breakdown.completenessScore ?? 0;
  const completenessSource = breakdown.completenessSource ?? "auto";
  const completenessDetail =
    breakdown.completenessDetail ?? "Customs field checklist on invoice text";

  return (
  <div style={{ minWidth: 0 }}>
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: compact ? 5 : 6,
        padding: compact ? "4px 6px" : "6px 8px",
        borderRadius: 4,
        background: passed ? PASS_BG : FAIL_BG,
        border: `1px solid ${passed ? PASS_BORDER : FAIL_BORDER}`,
      }}
    >
      {passed ? (
        <CheckCircleFilled
          style={{
            color: PASS_GREEN,
            fontSize: compact ? 13 : 15,
            marginTop: 0,
            flexShrink: 0,
          }}
        />
      ) : (
        <CloseCircleFilled
          style={{
            color: FAIL_RED,
            fontSize: compact ? 13 : 15,
            marginTop: 0,
            flexShrink: 0,
          }}
        />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text
          strong
          style={{
            fontSize: compact ? 11 : 12,
            lineHeight: 1.25,
            color: passed ? "#389e0d" : FAIL_RED,
          }}
        >
          Valid commercial invoice (not proforma)
        </Text>
        {breakdown.nonProformaDetail ? (
          <Text
            style={{
              display: "block",
              fontSize: compact ? 9 : 10,
              lineHeight: 1.25,
              color: passed ? "#52c41a" : "#cf1322",
            }}
          >
            {breakdown.nonProformaDetail}
          </Text>
        ) : null}
      </div>
    </div>

    <ScoreRow
      label="Field completeness"
      method={formatScoreMethod(completenessSource, completenessDetail, compact)}
      score={completenessScore}
      compact={compact}
    />
    <ScoreRow
      label="Booking field match"
      method={
        breakdown.matchSource
          ? formatScoreMethod(breakdown.matchSource, breakdown.matchDetail, compact)
          : undefined
      }
      score={breakdown.matchScore}
      detail={breakdown.matchScore == null ? breakdown.matchDetail : undefined}
      compact={compact}
    />

    {showAiNote ? (
      <Text type="secondary" style={{ fontSize: 9, display: "block", marginTop: compact ? 3 : 4, lineHeight: 1.3 }}>
        {aiCheckPending
          ? "Automatic scores shown · AI still running…"
          : breakdown.hasDeepCheck
            ? "Scores from AI analysis."
            : "Automatic scan only — run AI deep check for semantic review."}
      </Text>
    ) : null}

    {extra ? <div style={{ marginTop: compact ? 2 : 4 }}>{extra}</div> : null}
  </div>
  );
};

/** Minimal table cell: non-proforma tick + two scores. */
export const CommercialInvoiceValidityCellContent: React.FC<{
  breakdown: CommercialInvoiceValidityBreakdown;
}> = ({ breakdown }) => (
  <div style={{ lineHeight: 1.25, textAlign: "center" }}>
    <Text
      style={{
        fontSize: 12,
        display: "block",
        color: breakdown.isNonProforma ? PASS_GREEN : FAIL_RED,
        fontWeight: 600,
      }}
    >
      {breakdown.isNonProforma ? "✓ CI" : "✗ PI"}
    </Text>
    <Text type="secondary" style={{ fontSize: 10 }}>
      {breakdown.completenessScore}%
      {breakdown.matchScore != null ? ` · ${breakdown.matchScore}%` : ""}
    </Text>
  </div>
);
